import type { CountryCode } from './types';
import {
  getCountryByCode,
  getI18nLanguages, getI18nCountryNames, getI18nPhrases, getI18nCityNames,
  getI18nTemplates, getI18nCountryLanguages,
  fetchI18nLanguagesAsync, fetchI18nCountryNamesAsync, fetchI18nPhrasesAsync,
  fetchI18nCityNamesAsync, fetchI18nSubdivisionNamesAsync, fetchI18nTemplatesAsync,
  fetchI18nCountryLanguagesAsync,
  _setI18nData,
} from './data-source';
import type { I18nLanguage } from './db-supabase';
import { CALENDAR_YEAR } from './config';

export const YEAR = CALENDAR_YEAR;

/** Interpolate {placeholder} tokens in a template string */
/**
 * Insert a space at script boundaries (e.g. Thai↔Latin, Arabic↔Latin)
 * to prevent "เยือนThailand" rendering.
 */
function fixScriptBoundarySpacing(text: string): string {
  const nonLatin = '\\u0E00-\\u0E7F\\u0600-\\u06FF\\u0750-\\u077F\\u0980-\\u09FF\\u0900-\\u097F\\u4E00-\\u9FFF';
  const latin = 'A-Za-z0-9';
  const re1 = new RegExp(`([${nonLatin}])([${latin}])`, 'g');
  const re2 = new RegExp(`([${latin}])([${nonLatin}])`, 'g');
  return text.replace(re1, '$1 $2').replace(re2, '$1 $2');
}

// Some translations use localized placeholder names (e.g. German {Land} instead of {country}).
// Map known aliases back to the canonical English keys used in code.
const PLACEHOLDER_ALIASES: Record<string, string> = {
  Land: 'country', Pays: 'country', País: 'country', Negara: 'country',
  Name: 'name', Nombre: 'name', Nom: 'name', Nama: 'name',
  Jahr: 'year', Année: 'year', Año: 'year', Tahun: 'year',
  Flagge: 'flag', Drapeau: 'flag', Bandera: 'flag', Bendera: 'flag',
  Webseite: 'siteName', Site: 'siteName',
};

export function interpolateTemplate(template: string, vars: Record<string, string | number>): string {
  let result = template;
  for (const [k, v] of Object.entries(vars)) {
    result = result.replace(`{${k}}`, String(v));
  }
  // Replace remaining {Placeholder} patterns via alias map
  if (result.includes('{')) {
    result = result.replace(/\{(\w+)\}/g, (match, placeholder) => {
      const canonical = PLACEHOLDER_ALIASES[placeholder];
      return canonical && vars[canonical] !== undefined ? String(vars[canonical]) : match;
    });
  }
  return fixScriptBoundarySpacing(result);
}

// --- Lazy lookup maps (built once on first access) ---

let _langMap: Map<string, I18nLanguage> | null = null;
export function ensureLangMap(): Map<string, I18nLanguage> {
  if (!_langMap) {
    _langMap = new Map();
    for (const l of getI18nLanguages()) _langMap.set(l.code, l);
  }
  return _langMap;
}

let _countryNameMap: Map<string, { display: string; slug: string }> | null = null;
export function ensureCountryNameMap(): Map<string, { display: string; slug: string }> {
  if (!_countryNameMap) {
    _countryNameMap = new Map();
    for (const cn of getI18nCountryNames()) {
      _countryNameMap.set(`${cn.countryCode}:${cn.lang}`, { display: cn.display, slug: cn.slug });
    }
  }
  return _countryNameMap;
}

let _phraseMap: Map<string, { display: string; slug: string }> | null = null;
export function ensurePhraseMap(): Map<string, { display: string; slug: string }> {
  if (!_phraseMap) {
    _phraseMap = new Map();
    for (const p of getI18nPhrases()) {
      _phraseMap.set(`${p.phraseKey}:${p.lang}`, { display: p.display, slug: p.slug });
    }
  }
  return _phraseMap;
}

let _cityNameMap: Map<string, { display: string; slug: string }> | null = null;
export function ensureCityNameMap(): Map<string, { display: string; slug: string }> {
  if (!_cityNameMap) {
    _cityNameMap = new Map();
    for (const cn of getI18nCityNames()) {
      _cityNameMap.set(`${cn.cityId}:${cn.lang}`, { display: cn.display, slug: cn.slug });
    }
  }
  return _cityNameMap;
}

let _templateMap: Map<string, string> | null = null;
export function ensureTemplateMap(): Map<string, string> {
  if (!_templateMap) {
    _templateMap = new Map();
    for (const t of getI18nTemplates()) {
      _templateMap.set(`${t.templateKey}:${t.lang}`, t.template);
    }
  }
  return _templateMap;
}

// --- Accessor functions ---

/** Resolve a UI string key for a given language, with optional parameter interpolation.
 *  Falls back to English, then to the raw key if no translation exists. */
export function ui(key: string, lang: string = 'en', params?: Record<string, string | number>): string {
  const map = ensurePhraseMap();
  const entry = map.get(`${key}:${lang}`) ?? map.get(`${key}:en`);
  let value = entry?.display ?? key;
  if (params) {
    value = interpolateTemplate(value, params);
  }
  return value;
}

/** Resolve a ui phrase key with a compile-time fallback when DB has no entry. */
export function uiFb(key: string, fallback: string, lang: string = 'en', params?: Record<string, string | number>): string {
  const map = ensurePhraseMap();
  const entry = map.get(`${key}:${lang}`) ?? map.get(`${key}:en`);
  let value = entry?.display ?? fallback;
  if (params) {
    value = interpolateTemplate(value, params);
  }
  return value;
}

export function getCountryNameEntry(code: CountryCode, lang: string): { display: string; slug: string } {
  const map = ensureCountryNameMap();
  return map.get(`${code}:${lang}`) ?? map.get(`${code}:en`) ?? { display: code, slug: code.toLowerCase() };
}

export function getCountryNamesByCode(code: CountryCode): Record<string, { display: string; slug: string }> {
  const result: Record<string, { display: string; slug: string }> = {};
  for (const cn of getI18nCountryNames()) {
    if (cn.countryCode === code) {
      result[cn.lang] = { display: cn.display, slug: cn.slug };
    }
  }
  return result;
}

export function getPhraseEntry(key: string, lang: string): { display: string; slug: string } {
  const map = ensurePhraseMap();
  return map.get(`${key}:${lang}`) ?? map.get(`${key}:en`) ?? { display: key, slug: key };
}

export function getCityNameEntry(cityId: string, lang: string): { display: string; slug: string } | undefined {
  const map = ensureCityNameMap();
  return map.get(`${cityId}:${lang}`) ?? map.get(`${cityId}:en`);
}

export function getCityNamesByIdRecord(cityId: string): Record<string, { display: string; slug: string }> | undefined {
  const result: Record<string, { display: string; slug: string }> = {};
  let found = false;
  for (const cn of getI18nCityNames()) {
    if (cn.cityId === cityId) {
      result[cn.lang] = { display: cn.display, slug: cn.slug };
      found = true;
    }
  }
  return found ? result : undefined;
}

export function getTemplateStr(key: string, lang: string, vars: Record<string, string | number>): string {
  const map = ensureTemplateMap();
  const tpl = map.get(`${key}:${lang}`) ?? map.get(`${key}:en`);
  if (!tpl) return '';
  return interpolateTemplate(tpl, vars);
}

// --- Language/country helpers ---

let _countryLangMap: Map<string, string[]> | null = null;
function ensureCountryLangMap(): Map<string, string[]> {
  if (!_countryLangMap) {
    _countryLangMap = new Map();
    const langMap = ensureLangMap();
    for (const cl of getI18nCountryLanguages()) {
      const lang = langMap.get(cl.lang);
      if (!lang?.isActive) continue;
      let langs = _countryLangMap.get(cl.countryCode);
      if (!langs) {
        langs = [];
        _countryLangMap.set(cl.countryCode, langs);
      }
      langs.push(cl.lang);
    }
    // Sort each country's languages by sort_order (globals first), then locals at end
    for (const [, langs] of _countryLangMap) {
      langs.sort((a, b) => (langMap.get(a)?.sortOrder ?? 999) - (langMap.get(b)?.sortOrder ?? 999));
    }
  }
  return _countryLangMap;
}

export function getLanguagesForCountry(code: CountryCode): string[] {
  const clMap = ensureCountryLangMap();
  const fromJunction = clMap.get(code);
  if (fromJunction && fromJunction.length > 0) {
    return fromJunction;
  }
  // Fallback: computed logic for countries not in junction table
  const langMap = ensureLangMap();
  const globals = [...langMap.values()]
    .filter(l => l.isGlobal && l.isActive)
    .sort((a, b) => a.sortOrder - b.sortOrder)
    .map(l => l.code);
  const country = getCountryByCode(code);
  const local = country?.localLang;
  if (local && !globals.includes(local) && langMap.get(local)?.isActive) {
    globals.push(local);
  }
  return globals;
}

/** Returns the native display label for a language code */
export function getLangLabel(lang: string): string {
  const langMap = ensureLangMap();
  return langMap.get(lang)?.label || lang;
}

/** Returns the English name for a language code (for subtitle display) */
export function getLangLabelEn(lang: string): string | undefined {
  const langMap = ensureLangMap();
  return langMap.get(lang)?.labelEn ?? undefined;
}

/** Check if a language code uses right-to-left script */
export function isRtlLang(lang: string): boolean {
  const langMap = ensureLangMap();
  return langMap.get(lang)?.isRtl ?? false;
}

/** Convert internal lang code to BCP 47 hreflang value */
export function toHreflang(lang: string): string {
  const langMap = ensureLangMap();
  return langMap.get(lang)?.hreflang ?? lang;
}

/** Returns localized intro paragraph text for a country page */
export function getIntroText(lang: string, countryDisplay: string, eventCount: number): string {
  return getTemplateStr('country_intro', lang, { country: countryDisplay, count: eventCount, year: YEAR });
}

// --- preloadI18n: on-demand i18n loader (qoy-2) ---
//
// db-supabase's module-init prefetch no longer fetches the 7 i18n tables.
// Server-component entry points (layout.tsx, sitemap routes, search route)
// must `await preloadI18n()` before reading any sync i18n helper. The fetches
// themselves are cached cross-request via unstable_cache (1h TTL, qoy-1b);
// the Promise singleton below dedupes within the process so concurrent
// callers join the same in-flight fetch instead of racing.

let _preloadPromise: Promise<void> | null = null;

async function _doPreloadI18n(): Promise<void> {
  const [
    languages,
    countryNames,
    phrases,
    cityNames,
    subdivisionNames,
    templates,
    countryLanguages,
  ] = await Promise.all([
    fetchI18nLanguagesAsync(),
    fetchI18nCountryNamesAsync(),
    fetchI18nPhrasesAsync(),
    fetchI18nCityNamesAsync(),
    fetchI18nSubdivisionNamesAsync(),
    fetchI18nTemplatesAsync(),
    fetchI18nCountryLanguagesAsync(),
  ]);

  _setI18nData({
    languages,
    countryNames,
    phrases,
    cityNames,
    subdivisionNames,
    templates,
    countryLanguages,
  });

  // Force the lazy maps to rebuild from the freshly-set data on next access.
  _langMap = null;
  _countryNameMap = null;
  _phraseMap = null;
  _cityNameMap = null;
  _templateMap = null;
  _countryLangMap = null;
}

export function preloadI18n(): Promise<void> {
  if (!_preloadPromise) {
    _preloadPromise = _doPreloadI18n().catch(err => {
      // Reset so a later request can retry. unstable_cache will keep failed
      // fetches out of its cache automatically.
      _preloadPromise = null;
      throw err;
    });
  }
  return _preloadPromise;
}

/** Test-only: clear the preload singleton so a fresh preloadI18n() actually re-fetches. */
export function _resetPreloadI18nForTesting(): void {
  _preloadPromise = null;
  _langMap = null;
  _countryNameMap = null;
  _phraseMap = null;
  _cityNameMap = null;
  _templateMap = null;
  _countryLangMap = null;
}
